import type React from "react"
import { cn } from "@/lib/utils"

/**
 * The class PostHog's session replay treats as "mask the text inside this".
 *
 * `ph-mask` is rrweb's `maskTextClass` and PostHog's default for it — no
 * config makes this work, which is why the string is safe to put on an element
 * directly when `<Private>` would only add a redundant wrapper.
 */
export const PII_MASK_CLASS = "ph-mask"

type Props = {
  children: React.ReactNode
  className?: string
  /** Defaults to a span; pass "div", "td", etc. when a span would be invalid. */
  as?: React.ElementType
}

/**
 * Wraps text that identifies a person, so session replay records its shape but
 * not its content.
 *
 * Session replay is deliberately *not* masking all text — see
 * `lib/posthog.ts` — so that replays stay readable enough to be worth
 * watching. The trade is that identifying values have to be marked, and this
 * is how. Reach for it whenever a driver, applicant or staff member's name,
 * licence number, credential number, address or date of birth is rendered as
 * text on a page that records.
 *
 * Anything typed into an `<input>` is already masked by `maskAllInputs` and
 * needs nothing here. Neither do pages on the never-record list.
 *
 * ```tsx
 * <h1 className={PII_MASK_CLASS}>{driver.full_name}</h1>
 * <p><Private>{driver.full_name}</Private> gets the forms a move requires.</p>
 * ```
 */
export function Private({ children, className, as: Component = "span" }: Props) {
  return <Component className={cn(PII_MASK_CLASS, className)}>{children}</Component>
}
